/**
 * SubmitForApprovalButton Component
 * Button for employees to submit their draft OKRs for manager approval
 */

import React from 'react';
import { Button } from '../common/Button';
import { useSubmitForApproval, useApprovalEnabled } from '../../hooks';

export interface SubmitForApprovalButtonProps {
  okrId: string;
  currentStatus: string;
  isOwner: boolean;
  onSuccess?: () => void;
}

export const SubmitForApprovalButton: React.FC<SubmitForApprovalButtonProps> = ({
  okrId,
  currentStatus,
  isOwner,
  onSuccess,
}) => {
  // Check if approval workflow is enabled for this tenant
  const { data: approvalConfig, isLoading: isCheckingEnabled } = useApprovalEnabled();
  const approvalEnabled = approvalConfig?.enabled ?? false;

  // Submit for approval mutation
  const { mutate: submitForApproval, isPending } = useSubmitForApproval();

  // Only show button if:
  // 1. OKR status is 'draft'
  // 2. Approval workflow is enabled
  // 3. Current user is the OKR owner
  const shouldShow = currentStatus === 'draft' && approvalEnabled && isOwner;

  // Don't render anything if conditions aren't met
  if (!shouldShow || isCheckingEnabled) {
    return null;
  }

  const handleSubmit = () => {
    submitForApproval(okrId, {
      onSuccess: () => {
        // Call parent's onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
      },
    });
  };

  return (
    <Button
      variant="primary"
      onClick={handleSubmit}
      isLoading={isPending}
      disabled={isPending}
    >
      {isPending ? 'Submitting...' : 'Submit for Approval'}
    </Button>
  );
};
